import { Card, CardActionArea, CardContent, Typography, Stack, Box, Chip, IconButton } from "@mui/material";
import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Post, deletePost } from "../api/posts";
import VoteButtons from "./VoteButtons";
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { timeAgo } from "../utils/timeAgo";
import DeleteConfirmDialog from "./DeleteConfirmDialog";

type Props = {
    post: Post;
    myVote: number;
    onVoted: () => void;
    currentUserId?: number | null;
    onDelete?: () => void;
};

export default function PostCard({ post, myVote, onVoted, currentUserId, onDelete }: Props) {
  const nav = useNavigate();
  const [openDelete, setOpenDelete] = useState(false);

  const handleDeleteClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setOpenDelete(true);
  };

  const confirmDelete = async () => {
      try {
          await deletePost(post.post_id);
          if (onDelete) onDelete();
      } catch(e) {
          alert("Failed to delete post");
      } finally {
          setOpenDelete(false);
      }
  };

  return (
    <>
    <motion.div
      layout
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
    >
    <Card variant="outlined">
      <Box sx={{ display: 'flex' }}>

        
        <CardActionArea onClick={() => nav(`/posts/${post.post_id}`)} sx={{ flexGrow: 1 }}>
            <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Chip label={post.topic_name || "General"} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: 'action.selected' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Posted by {post.username} • {timeAgo(post.created_at)}
                        </Typography>
                    </Stack>
                    {currentUserId === post.user_id && (
                        <IconButton size="small" onClick={handleDeleteClick} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
                
                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ lineHeight: 1.3, fontSize: '1.15rem' }}>
                    {post.title}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxHeight: 44, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.5 }}>
                    {post.content}
                </Typography>

                <Stack direction="row" spacing={3} alignItems="center" sx={{ mt: 2 }}>
                    <Box onClick={(e) => e.stopPropagation()}>
                        <VoteButtons postId={post.post_id} score={post.vote_score} myVote={myVote} onVoted={onVoted} />
                    </Box>

                    <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
                        <ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />
                        <Typography variant="body2" fontWeight={500}>
                            {post.comment_count || 0} Comments
                        </Typography>
                    </Stack>
                </Stack>

                {/* Top Comments Preview */}
                {post.top_comments && post.top_comments.length > 0 && (
                    <Box sx={{ mt: 2, bgcolor: 'action.hover', p: 1.5, borderRadius: 2 }}>
                        {post.top_comments.map((c, i) => (
                            <Box key={i} sx={{ mb: i < (post.top_comments?.length || 0) - 1 ? 1 : 0 }}>
                                <Typography variant="caption" fontWeight={700} sx={{ mr: 1 }}>
                                    {c.username}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {c.content}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </CardContent>
        </CardActionArea>
      </Box>
    </Card>
    </motion.div>
    <DeleteConfirmDialog 
        open={openDelete} 
        onClose={() => setOpenDelete(false)} 
        onConfirm={confirmDelete}
        title="Delete Post?"
        description="Are you sure you want to delete this post? This action cannot be undone."
    />
    </>
  );
}
